import { COLUMN_WIDTHS } from '@bridge/constants';
import { formatDate } from '@bridge/utils';

import { createColumn } from './createColumn';

import type { InlineColumnFactory } from './createColumn';
import type { ColumnFactory } from './types';

interface TimestampColumnOptions<TData> {
  accessor?: keyof TData & string;
  label?: string;
  sortable?: boolean;
  size?: number;
  showTime?: boolean;
  relative?: boolean;
}

function createTimestampColumn<TData>(
  defaultAccessor: keyof TData & string,
  defaultLabel: string,
  options?: TimestampColumnOptions<TData>,
): ColumnFactory<TData> {
  const {
    accessor = defaultAccessor,
    label = defaultLabel,
    sortable = false,
    size,
    showTime,
    relative,
  } = options ?? {};

  return createColumn<TData>({
    type: 'date',
    accessor,
    label,
    options: {
      size: size ?? (showTime || relative ? COLUMN_WIDTHS.dateTime : COLUMN_WIDTHS.dateShort),
      sortable,
      useTooltip: true,
      dateDisplayProps: { showTime, relative },
    },
  });
}

/**
 * Creates a standard "Date created" column with short date formatting.
 *
 * @example
 * ```ts
 * const DATE = createDateCreatedColumn<Customer>();
 * const DATE = createDateCreatedColumn<Customer>({ label: 'Date added', sortable: true });
 * ```
 */
export function createDateCreatedColumn<TData>(options?: {
  accessor?: keyof TData & string;
  label?: string;
  sortable?: boolean;
  size?: number;
  formatter?: (date: Date | string) => string;
}): InlineColumnFactory<TData>;
export function createDateCreatedColumn<TData>(options?: {
  accessor?: keyof TData & string;
  label?: string;
  sortable?: boolean;
  size?: number;
  formatter?: (date: Date | string) => string;
}): ColumnFactory<TData> {
  const {
    accessor = 'createdAt' as keyof TData & string,
    label = 'Date created',
    sortable = false,
    size = COLUMN_WIDTHS.dateShort,
    formatter = (date: Date | string) =>
      formatDate(new Date(date), { month: '2-digit', day: '2-digit', year: '2-digit' }),
  } = options ?? {};

  return createColumn<TData>({
    type: 'date',
    accessor,
    label,
    options: { size, sortable, formatter },
  });
}

/**
 * Creates a standard "Created" column rendered with DateDisplay (UTC/local tooltip).
 *
 * @example
 * ```ts
 * const CREATED = createCreatedAtColumn<WireRecord>();
 * const CREATED_WITH_TIME = createCreatedAtColumn<WireRecord>({ showTime: true });
 * ```
 */
export function createCreatedAtColumn<TData>(options?: TimestampColumnOptions<TData>): InlineColumnFactory<TData>;
export function createCreatedAtColumn<TData>(options?: TimestampColumnOptions<TData>): ColumnFactory<TData> {
  return createTimestampColumn<TData>('created_at' as keyof TData & string, 'Created', options);
}

/**
 * Creates a standard "Updated" column rendered with DateDisplay (UTC/local tooltip).
 *
 * @example
 * ```ts
 * const UPDATED = createUpdatedAtColumn<WireRecord>();
 * const UPDATED_RELATIVE = createUpdatedAtColumn<WireRecord>({ relative: true });
 * ```
 */
export function createUpdatedAtColumn<TData>(options?: TimestampColumnOptions<TData>): InlineColumnFactory<TData>;
export function createUpdatedAtColumn<TData>(options?: TimestampColumnOptions<TData>): ColumnFactory<TData> {
  return createTimestampColumn<TData>('updated_at' as keyof TData & string, 'Updated', options);
}

/**
 * Creates a standard copyable ID column with truncation.
 *
 * @example
 * ```ts
 * const ID = createIdColumn<CardAccount>({ label: 'Card ID' });
 * const ID = createIdColumn<Transaction>({ label: 'Transaction ID', accessor: 'transactionId' });
 * ```
 */
export function createIdColumn<TData>(options: {
  label: string;
  accessor?: keyof TData & string;
  size?: number;
  maxWidth?: number;
}): InlineColumnFactory<TData>;
export function createIdColumn<TData>(options: {
  label: string;
  accessor?: keyof TData & string;
  size?: number;
  maxWidth?: number;
}): ColumnFactory<TData> {
  const { label, accessor = 'id' as keyof TData & string, size = COLUMN_WIDTHS.idFull, maxWidth = 150 } = options;

  return createColumn<TData>({
    type: 'copyable',
    accessor,
    label,
    options: { size, maxWidth, truncate: true },
  });
}
